import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { api, apiSchemas, TPermission } from '@verifime/api-definition';
import {
  CircularProgressButton,
  ColoredIcon,
  ContentDetailsDisplay,
  DisplayErrors,
  enqueSuccessNotification,
  generateNonFieldErrors,
  NewPermission,
  PermissionStatus,
  TApproachOfIdentityToShare,
  TIdentity,
  TPermissionOrganisation,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { SERVER_ERRORS } from '@verifime/utils';
import { debounce } from 'lodash';
import React from 'react';
import { z } from 'zod';
import { HELD_DOCUMENT_TYPE_NOT_ACCEPTED_MESSAGE } from '../../EntitySelection/ConfirmEntityView/confirmEntityCopy';
import { isHeldDocumentTypeRefusal } from '../../EntitySelection/hooks/heldDocumentTypeRefusal';
import { useAPIPermissions } from '../hooks';

const NO_SELECTED_PERMISSION = 'Please select an organisation to grant the permission';
const PERMISSION_GRANTED_SUCCESS = 'Your permission record has been submitted.';

export default function NewCustomerPermission({
  initialSelectedIdentity,
  identities,
  open,
  onClose,
  initialOrganisation,
  isOrganisationModifiable = true,
  onGrantSuccess,
  approachOfIdentityToShare,
  onSelectedIdentityChange,
  adminOverride = false,
}: Readonly<{
  initialSelectedIdentity?: TIdentity;
  identities: TIdentity[];
  open: boolean;
  onClose: () => void;
  initialOrganisation?: TPermissionOrganisation;
  isOrganisationModifiable?: boolean;
  onGrantSuccess?: (permission: TPermission) => void;
  approachOfIdentityToShare?: TApproachOfIdentityToShare;
  onSelectedIdentityChange?: (identity: TIdentity) => void;
  adminOverride?: boolean;
}>) {
  const [searchName, setSearchName] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [permissionOrganisations, setPermissionOrganisations] = React.useState<
    z.infer<typeof apiSchemas.OrgSummarySearchResult>[]
  >(initialOrganisation ? [initialOrganisation] : []);
  const [selectedIdentity, setSelectedIdentity] =
    React.useState<TIdentity>(initialSelectedIdentity);
  const [selectedOrganisation, setSelectedOrganisation] =
    React.useState<TPermissionOrganisation>(initialOrganisation);
  const [error, setError] = React.useState<string>(undefined);

  const { permissions, isLoadingPermissions } = useAPIPermissions(selectedIdentity?.id);

  // the component is reused for different identities,
  // the initialSelectedIdentity is used to represent the default identity of the drop down
  // which can be further updated with dropdown selection.  The initialSelectedIdentity, will not
  // change when this component (drawer component) is opened. However it might be changed when
  // the drawer is closed and reopened, hence changing the dropdown selection would not be conflict
  // with user's choice.
  React.useEffect(() => {
    setSelectedIdentity(initialSelectedIdentity);
  }, [initialSelectedIdentity]);

  const debouncedSearchHandler = React.useMemo(
    () =>
      debounce((newSearchName: string) => {
        setSearchName(newSearchName);
      }, 500),
    [],
  );

  const customerId = selectedIdentity?.id;
  const getOrganisationSummary = React.useCallback(async () => {
    try {
      setError(undefined);
      setIsLoading(true);
      setPermissionOrganisations([]);

      const searchCriteria = searchName.trim();
      if (customerId && searchCriteria) {
        setPermissionOrganisations(
          await api
            .getV1organisationsummarycustomerCustomerId({
              params: {
                customerId,
              },
              queries: { criteria: searchCriteria },
            })
            .then((results) =>
              results.map((result) => {
                return {
                  organisationId: result.subEntityUUID,
                  ...result,
                };
              }),
            ),
        );
      }
    } catch (error) {
      setError('Search failed.');
    } finally {
      setIsLoading(false);
    }
  }, [searchName, customerId]);

  React.useEffect(() => {
    if (searchName === '') {
      setPermissionOrganisations([]);
      return;
    }

    if (searchName === selectedOrganisation?.subEntityName) {
      return;
    }

    getOrganisationSummary();
  }, [getOrganisationSummary, searchName, selectedOrganisation?.subEntityName]);

  const handleGrantPermission = async (formData: any) => {
    if (!selectedOrganisation) {
      setError(NO_SELECTED_PERMISSION);
      return;
    }
    setError(undefined);
    setIsLoading(true);

    try {
      const { permission, validationResults } = await api.postV1permission({
        customerId: formData.identity,
        organisationId: selectedOrganisation.organisationId,
        adminOverride: adminOverride || undefined,
      });

      if (validationResults?.length > 0) {
        const nonFieldErrors = generateNonFieldErrors(validationResults);
        setError(nonFieldErrors[0].message as string);
        return;
      }

      setSearchName('');
      enqueSuccessNotification(PERMISSION_GRANTED_SUCCESS);
      onGrantSuccess?.(permission);
    } catch (err: any) {
      console.error('Error granting permission:', err);
      if (isHeldDocumentTypeRefusal(err)) {
        setError(HELD_DOCUMENT_TYPE_NOT_ACCEPTED_MESSAGE);
        return;
      }
      const { message } = err || { code: 'default', message: SERVER_ERRORS.default };
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const { handleSubmit, control, reset } = useCustomForm();

  // The slot shows the shared server error for any failure, except person-api's refusal of a
  // customer who holds a photo card or ImmiCard that the client has not opted into: that is news
  // the customer acts on, so it shows its own message and is announced.
  const isHeldDocumentTypeRefusalShown = error === HELD_DOCUMENT_TYPE_NOT_ACCEPTED_MESSAGE;

  const alreadyGrantedPermission = permissions?.some(
    (permission) =>
      permission.organisationId === selectedOrganisation?.organisationId &&
      permission.permissionStatus === PermissionStatus.Active,
  );

  return (
    <ContentDetailsDisplay
      isOpen={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxWidth: tokens.sizeSm,
          padding: tokens.spacingBase,
        },
      }}
    >
      <Stack gap={tokens.spacingBase} height="100%">
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flex={1}>
          <Typography variant="h5">New Permission</Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <ColoredIcon iconName="closeRounded" fontSize="medium" />
          </IconButton>
        </Stack>

        <Stack flex={20} justifyContent="space-between">
          <Box component="form">
            <NewPermission
              control={control}
              reset={reset}
              selectedIdentity={selectedIdentity}
              identities={identities}
              initialOrganisation={initialOrganisation}
              isOrganisationModifiable={isOrganisationModifiable}
              orgSearchProps={{
                options: permissionOrganisations.map((org) => ({
                  organisationId: org.subEntityUUID,
                  subEntityName: org.subEntityName,
                  reportingEntityName: org.reportingEntityName,
                  subEntityId: org.subEntityId,
                })),
                isSearching: isLoading,
                handleInputChange: (_, searchName) => {
                  debouncedSearchHandler(searchName);
                },
                handleValueChange: (_, organisation) => {
                  setSelectedOrganisation(organisation as TPermissionOrganisation);
                },
              }}
              handleChangeOrganisationSelection={() => {
                setSearchName('');
                setSelectedOrganisation(undefined);
              }}
              handleChangeSelectedIdentity={(newlySelectedIdentity) => {
                setSelectedIdentity(newlySelectedIdentity);
                onSelectedIdentityChange?.(newlySelectedIdentity);
              }}
              approachOfIdentityToShare={approachOfIdentityToShare}
            />
          </Box>
          <Stack direction="column">
            {error && (
              <DisplayErrors
                role={isHeldDocumentTypeRefusalShown ? 'alert' : undefined}
                errors={[
                  { message: isHeldDocumentTypeRefusalShown ? error : SERVER_ERRORS.default },
                ]}
              />
            )}
            <Stack
              direction="row-reverse"
              alignItems="flex-start"
              justifyItems="flex-start"
              spacing={tokens.spacingBase}
            >
              <CircularProgressButton
                variant="contained"
                size="medium"
                sx={{
                  borderRadius: tokens.borderRadiusSm,
                  padding: `${tokens.spacingXs} ${tokens.spacingBase}`,
                }}
                disabled={!selectedOrganisation || isLoading || alreadyGrantedPermission}
                isShowProgress={isLoading || isLoadingPermissions}
                onClick={handleSubmit(handleGrantPermission)}
              >
                {isLoadingPermissions
                  ? 'Loading'
                  : alreadyGrantedPermission
                    ? 'Already Granted'
                    : 'Grant Permission'}
              </CircularProgressButton>
              <Button variant="text" size="medium" onClick={onClose}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </ContentDetailsDisplay>
  );
}
